import {
  createResource,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import { useNavigate } from "@solidjs/router";
import type { AppNotification } from "@energyplatform/client";
import { api } from "../services.ts";
import { locale, t } from "../i18n.ts";
import { formatAgo } from "../format.ts";
import { eventTitle, ruleOf } from "../events.ts";
import { Icon } from "../icons.tsx";

const REFRESH_MS = 60_000;

/**
 * Notifications of the signed-in person (prototype bell): events of rules that name them as recipients, escalations,
 * reminders and assignments. Checked every minute; opening one marks it read and shows the event.
 */
export function Bell() {
  const navigate = useNavigate();
  const [open, setOpen] = createSignal(false);
  const [onlyUnread, setOnlyUnread] = createSignal(false);
  const [notes, { refetch }] = createResource(() => api.events.notifications());
  const [events] = createResource(open, () => api.events.list());
  const [rules] = createResource(open, () => api.events.rules());
  // Names of generated reports, read only when a report notification is open.
  const [reports] = createResource(
    () => open() && (notes() ?? []).some((n) => n.snapshotId !== null),
    async () => {
      const [defs, snaps] = await Promise.all([
        api.reports.list(),
        api.reports.snapshots(),
      ]);
      return new Map(
        snaps.map((s) => [
          s.id,
          defs.find((d) => d.id === s.reportId)?.name ?? "",
        ]),
      );
    },
  );
  let root: HTMLDivElement | undefined;
  const reload = () => {
    api.invalidate("/api/v1/notifications");
    void refetch();
  };
  onMount(() => {
    const timer = setInterval(reload, REFRESH_MS);
    const outside = (e: MouseEvent) =>
      root && !root.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", outside);
    onCleanup(() => {
      clearInterval(timer);
      document.removeEventListener("mousedown", outside);
    });
  });
  const unread = () => (notes() ?? []).filter((n) => n.readAt === null).length;
  const shown = () =>
    (notes() ?? []).filter((n) => !onlyUnread() || n.readAt === null);
  const title = (n: AppNotification) => {
    if (n.snapshotId !== null) {
      return reports()?.get(n.snapshotId) || t("notif.report");
    }
    const e = events()?.find((x) => x.id === n.eventId);
    return e ? eventTitle(e, ruleOf(e, rules())) : t("notif.event");
  };
  const openOne = async (n: AppNotification) => {
    await api.events.markRead([n.id]);
    reload();
    setOpen(false);
    navigate(
      n.snapshotId !== null
        ? `/reports?tab=preview&snapshot=${encodeURIComponent(n.snapshotId)}`
        : `/events/${encodeURIComponent(n.eventId ?? "")}`,
    );
  };

  return (
    <div class="cw" ref={root}>
      <button
        type="button"
        class="bell"
        aria-expanded={open()}
        aria-haspopup="dialog"
        aria-label={t("notif.aria", { count: unread() })}
        title={t("notif.title")}
        onClick={() => setOpen(!open())}
      >
        <Icon name="bell" />
        <Show when={unread() > 0}>
          <span class="badge">{unread()}</span>
        </Show>
      </button>
      <Show when={open()}>
        <div
          class="pop right notif"
          role="dialog"
          aria-label={t("notif.title")}
        >
          <div class="np-h">
            <h3>{t("notif.title")}</h3>
            <div class="seg" style={{ "margin-left": "auto" }}>
              <button
                type="button"
                aria-pressed={!onlyUnread()}
                onClick={() => setOnlyUnread(false)}
              >
                {t("notif.all")}
              </button>
              <button
                type="button"
                aria-pressed={onlyUnread()}
                onClick={() => setOnlyUnread(true)}
              >
                {t("notif.unread", { count: unread() })}
              </button>
            </div>
          </div>
          <ul class="nlist">
            <For
              each={shown()}
              fallback={
                <li class="hint" style={{ padding: "14px 12px" }}>
                  {t("notif.none")}
                </li>
              }
            >
              {(n) => (
                <li>
                  <button
                    type="button"
                    class={`ni ${n.readAt ? "" : "unread"}`}
                    onClick={() => void openOne(n)}
                  >
                    <span class="nd" />
                    <span style={{ "min-width": 0 }}>
                      <span class="eyebrow">
                        {t(`notif.kind.${n.kind}`)} ·{" "}
                        {formatAgo(n.at, locale())}
                      </span>
                      <b style={{ display: "block" }}>{title(n)}</b>
                      <span class="hint" style={{ display: "block" }}>
                        {t("notif.sent", { channels: t("events.channel.app") })}
                      </span>
                    </span>
                  </button>
                </li>
              )}
            </For>
          </ul>
          <div class="np-f">
            <button
              type="button"
              class="btn ghost sm"
              disabled={unread() === 0}
              onClick={() => void api.events.markRead(null).then(reload)}
            >
              <Icon name="check" />
              {t("notif.markAll")}
            </button>
            <button
              type="button"
              class="btn ghost sm"
              onClick={() => {
                setOpen(false);
                navigate("/events?tab=rules");
              }}
            >
              <Icon name="bell" />
              {t("notif.rules")}
            </button>
          </div>
        </div>
      </Show>
    </div>
  );
}
